import React, { useEffect, useState } from "react";
import api, { formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, Empty } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, ShieldCheck, Trash2 } from "lucide-react";

export default function RolesPermissions() {
    const [data, setData] = useState(null);      // {roles, modules, base_roles}
    const [dirty, setDirty] = useState({});      // {roleKey: Set(modules)}
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: "", inherits: [], modules: [] });

    const load = () => api.get("/admin/rbac").then((r) => { setData(r.data); setDirty({}); }).catch((e) => toast.error(formatErrorDetail(e.response?.data?.detail)));
    useEffect(() => { load(); }, []);

    if (!data) return null;
    const roles = data.roles.filter((r) => r.key !== "super_admin"); // super admin always has everything
    const mods = data.modules;

    const currentMods = (role) => dirty[role.key] ? dirty[role.key] : new Set(role.modules);
    const toggle = (role, mkey) => {
        const s = new Set(currentMods(role));
        s.has(mkey) ? s.delete(mkey) : s.add(mkey);
        setDirty((d) => ({ ...d, [role.key]: s }));
    };
    const save = async (role) => {
        try {
            await api.put(`/admin/rbac/roles/${role.key}/modules`, { modules: [...currentMods(role)] });
            toast.success(`${role.name} updated`);
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };
    const removeRole = async (role) => {
        if (!window.confirm(`Delete role “${role.name}”?`)) return;
        try { await api.delete(`/admin/rbac/roles/${role.key}`); toast.success("Role deleted"); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };
    const createRole = async () => {
        if (!form.name.trim()) return toast.error("Role name required");
        if (!form.inherits.length) return toast.error("Pick at least one base role it inherits rights from");
        try {
            await api.post("/admin/rbac/roles", form);
            toast.success("Role created — assign it to users in Users & Roles");
            setCreating(false); setForm({ name: "", inherits: [], modules: [] }); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <PageHeader title="Roles & Permissions" subtitle="Control which modules each role can use, and create custom roles. Custom roles inherit backend rights from a base role; module toggles control what they see."
                actions={
                    <Dialog open={creating} onOpenChange={setCreating}>
                        <DialogTrigger asChild><button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="new-role-btn"><Plus className="size-4" /> New Role</button></DialogTrigger>
                        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                            <DialogHeader><DialogTitle>Create a role</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <div><Label>Role name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Branch HR" data-testid="role-name" /></div>
                                <div>
                                    <Label>Inherits rights from (base roles)</Label>
                                    <div className="grid grid-cols-2 gap-1 mt-1">
                                        {data.base_roles.map((b) => (
                                            <label key={b} className="flex items-center gap-2 text-sm">
                                                <input type="checkbox" checked={form.inherits.includes(b)} onChange={(e) => setForm({ ...form, inherits: e.target.checked ? [...form.inherits, b] : form.inherits.filter((x) => x !== b) })} />
                                                {b.replace(/_/g, " ")}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <Label>Visible modules</Label>
                                    <div className="grid grid-cols-2 gap-1 mt-1 max-h-56 overflow-y-auto border rounded-md p-2">
                                        {mods.map((m) => (
                                            <label key={m.key} className="flex items-center gap-2 text-xs">
                                                <input type="checkbox" checked={form.modules.includes(m.key)} onChange={(e) => setForm({ ...form, modules: e.target.checked ? [...form.modules, m.key] : form.modules.filter((x) => x !== m.key) })} />
                                                {m.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={createRole} data-testid="role-create">Create role</button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                } />

            <Surface className="p-0 overflow-x-auto">
                <table className="w-full dense-table" style={{ minWidth: 900 }}>
                    <thead>
                        <tr>
                            <th className="sticky left-0 bg-background z-10">Module</th>
                            {roles.map((r) => (
                                <th key={r.key} className="text-center">
                                    <div className="flex flex-col items-center gap-1">
                                        <span className="whitespace-nowrap">{r.name}</span>
                                        {!r.built_in && <span className="badge badge-info">custom</span>}
                                        <div className="flex items-center gap-1">
                                            {dirty[r.key] && <button className="text-[10px] text-primary font-semibold" onClick={() => save(r)} data-testid={`save-${r.key}`}>Save</button>}
                                            {!r.built_in && <button className="text-destructive" onClick={() => removeRole(r)} title="Delete role"><Trash2 className="size-3" /></button>}
                                        </div>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {mods.map((m) => (
                            <tr key={m.key}>
                                <td className="sticky left-0 bg-background z-10 text-xs font-medium whitespace-nowrap">{m.label}</td>
                                {roles.map((r) => (
                                    <td key={r.key} className="text-center">
                                        <input type="checkbox" checked={currentMods(r).has(m.key)} onChange={() => toggle(r, m.key)} data-testid={`chk-${r.key}-${m.key}`} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Surface>
            <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><ShieldCheck className="size-3" /> Super Admin always has every module. Changes take effect the next time users load the app.</div>
        </div>
    );
}
